import type { Component } from 'vue';
import { Fragment, h } from 'vue';
import parse from 'html-parser-lite';

const VALID_COMPONENTS = [
  'CiSubscribeTopic',
  'CiLinkButton',
];

export function useCompiler() {
  function extractComponentNames(html: string): string[] {
    const matches = html.match(/<ci-[^>]+>/g) || [];
    return [...new Set(matches.map(match => match.slice(1, -1).split(' ')[0]))];
  }

  async function loadComponent(componentName: string) {
    // // Convert kebab-case to PascalCase
    const pascalCaseName = componentName
      .split('-')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join('');

    if (!VALID_COMPONENTS.includes(pascalCaseName)) {
      console.error(`Invalid component: ${componentName}`);
      return null;
    }

    return defineAsyncComponent(() =>
      import(`@/components/slots/dynamic/${pascalCaseName}/${pascalCaseName}.vue`),
    );
  }

  function buildElement(element: any, components: Record<string, Component>) {
    if (element.nodeType === 1) {
      const { attrs } = element;
      const name = element.tagName;
      const children = compilerChildren(element.childNodes, components);
      const component = components[element.tagName];
      return component
        ? h(component, attrs, {
            default: () => children,
          })
        : h(name, attrs, children);
    }

    return element.textContent;
  }

  function compilerChildren(parent: Node[], components: Record<string, Component>) {
    const children = [];
    Array.from(parent).forEach((item) => {
      switch (item.nodeType) {
        case 1:
        case 3:
          children.push(buildElement(item, components));
          break;
        default:
          console.warn('Can\'t resolve this element: ', item);
      }
    });

    return children;
  }

  function compiler(html: string, components: Record<string, Component>) {
    const comps = Object.fromEntries(
      Object.entries(components).map(([name, component]) => [name.toLowerCase(), component]),
    );

    return () => h(Fragment, null, compilerChildren(
      parse(html.replaceAll('&amp;', '&')),
      comps,
    ));
  }

  return {
    compiler,
    extractComponentNames,
    loadComponent,
  };
}
